import ExcelJS from 'exceljs';
import { csvExporter } from '../../../../shared/csv';
import { exportPDF } from '../../../../shared/helpers';
import { IExpirationGroups } from './utils';
import { UserOptions } from 'jspdf-autotable';
import { saveAs } from 'file-saver';

type ExportRow = {
    'Vehicle Name': string;
    'Type': string;
    'Expiration Date': string;
    'Days Left': string | number;
};

const formatDaysLeft = (daysLeft: number): string | number => {
    if (daysLeft === 0) return 'Today';
    if (daysLeft > 0) return daysLeft;
    return 'Expired';
};

const toRows = (data: IExpirationGroups): ExportRow[] => {
    const rows: ExportRow[] = [];
    const addItems = (items: IExpirationGroups[keyof IExpirationGroups]) => {
        items.forEach((item) => {
            rows.push({
                'Vehicle Name': item.vehicleName,
                'Type': item.itemName,
                'Expiration Date': item.expireDate,
                'Days Left': formatDaysLeft(item.daysLeft),
            });
        });
    };

    addItems(data.expired);
    addItems(data.days30);
    addItems(data.days60);
    addItems(data.days90);

    return rows;
};

export const exportExpirationReportCsv = (data: IExpirationGroups) => {
    const rows = toRows(data);
    const header = ['Vehicle Name', 'Type', 'Expiration Date', 'Days Left'];
    const body = rows.map((row) => [
        row['Vehicle Name'],
        row['Type'],
        row['Expiration Date'],
        row['Days Left'],
    ]);

    csvExporter('ExpirationsReport.csv')([header, ...body]);
};

export const exportExpirationReportXlsx = (data: IExpirationGroups) => {
    const rows = toRows(data);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Expirations');

    // Define columns with headers and widths
    ws.columns = [
        { header: 'Vehicle Name', key: 'Vehicle Name', width: 25 },
        { header: 'Type', key: 'Type', width: 20 },
        { header: 'Expiration Date', key: 'Expiration Date', width: 18 },
        { header: 'Days Left', key: 'Days Left', width: 12 },
    ];

    // Add data rows
    rows.forEach(row => {
        ws.addRow(row);
    });

    // Set font size for all cells
    ws.columns.forEach((col) => {
        col.eachCell({ includeEmpty: true }, (cell) => {
            cell.font = { name: 'Calibri', size: 8 };
        });
    });

    // Write and save the file
    wb.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, 'ExpirationsReport.xlsx');
    });
};

export const exportExpirationReportPdf = (data: IExpirationGroups) => {
    const rows = toRows(data).map((row) => [
        row['Vehicle Name'],
        row['Type'],
        row['Expiration Date'],
        row['Days Left'],
    ]);

    const table: UserOptions = {
        head: [['Vehicle Name', 'Type', 'Expiration Date', 'Days Left']],
        body: rows,
        styles: { fontSize: 9 },
    };

    exportPDF({ fileName: 'ExpirationsReport.pdf', data: [table] });
};
